import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import {
  selectProject,
  saveProjectData,
  toggleProjectDialog,
} from '../../actions'
import { useState } from 'react'
import uuid from 'uuid'
import {
  checkNullorEmptyObj,
  getDistanceUnits,
  getMassUnits,
  getSpeedUnits,
} from '../../../../lib/util'
import ProjectDialogMap from './ProjectDialogMap'
import Geocoder from './Geocoder'
import { DEFAULT_PROJECT_CENTER } from '../../../../lib/const'
import UnitsSelect from '../UI/UnitsSelect'

const PREFIX = 'Project-dialog'
const classes = {
  main: `${PREFIX}`,
}

const PaperStyled = styled(Paper)({
  width: '80%',
  margin: '10% auto 0',
  padding: '0 20px',
  [`&.${classes.main} .MuiTypography-h6`]: {
    textAlign: 'center',
  },
  [`&.${classes.main} .MuiOutlinedInput-input`]: {
    padding: '10.5px 14px',
  },
  [`&.${classes.main} .MuiFormLabel-root`]: {
    color: 'color: gray !important;',
    fontSize: 14,
  },
  [`&.${classes.main} .mapboxgl-ctrl-geocoder.mapboxgl-ctrl`]: {
    width: '100%',
    border: '1px solid #bbb',
    boxShadow: 'none',
    borderRadius: '4px',
    marginTop: '8px',
    marginRight: '0',
  },
  [`&.${classes.main} .mapboxgl-ctrl-geocoder--input`]: {
    height: '40px',
  },
  [`&.${classes.main} .mapboxgl-ctrl-geocoder--icon`]: {
    top: '10px',
  },
})

export default function FormDialog() {
  const dispatch = useDispatch()
  const { remoteStorage, projectDialogOpen } = useSelector(state => state)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [massUnits, setMassUnits] = useState(getMassUnits()[0])
  const [distanceUnits, setDistanceUnits] = useState(getDistanceUnits()[0])
  const [speedUnits, setSpeedUnits] = useState(getSpeedUnits()[0])
  const [error, setError] = useState(null)
  const [center, setCenter] = useState(DEFAULT_PROJECT_CENTER)

  const validationResults = () => {
    let result = {}
    if (!name) {
      result['name'] = {
        message: 'Please enter a name.',
      }
    }
    if (!description) {
      result['description'] = {
        message: 'Please enter a description.',
      }
    }
    return result
  }

  const validationMessage = (validationResults, field) => {
    return (
      !!validationResults &&
      validationResults[field] &&
      validationResults[field].message
    )
  }

  const handleNameChange = e => {
    setName(e.target.value)
    if (error && error['name'] && e.target.value) {
      setError({ ...error, name: null })
    }
  }

  const handleDescriptionChange = e => {
    setDescription(e.target.value)
    if (error && error['description'] && e.target.value) {
      setError({ ...error, description: null })
    }
  }

  const reset = () => {
    setName('')
    setDescription('')
    setMassUnits(getMassUnits()[0])
    setDistanceUnits(getDistanceUnits()[0])
    setSpeedUnits(getSpeedUnits()[0])
    setError(null)
    setCenter(DEFAULT_PROJECT_CENTER)
  }

  const close = () => {
    dispatch(toggleProjectDialog(false))
    reset()
  }

  const submit = () => {
    const project = {
      name,
      description,
      center: `{"lat":${center[1]},"lon":${center[0]}}`,
      geojson: '',
      units: { massUnits, distanceUnits, speedUnits },
    }

    if (!checkNullorEmptyObj(validationResults())) {
      setError(validationResults())
      return false
    }

    if (remoteStorage.projects) {
      //todo: save new project to DB
    } else {
      project.id = uuid()
    }
    dispatch(saveProjectData(project))
    dispatch(selectProject(project.id))
    dispatch(toggleProjectDialog(false))
    reset()
  }

  return (
    <Modal open={!!projectDialogOpen} onClose={close}>
      <PaperStyled className={classes.main}>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">New Project</Typography>
          </Grid>
        </Grid>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid item xs={6}>
            <TextField
              error={!!validationMessage(error, 'name')}
              autoFocus
              margin="dense"
              id="name"
              label="Name"
              variant="outlined"
              value={name}
              fullWidth
              onChange={handleNameChange}
              helperText={validationMessage(error, 'name')}
            />
            <TextField
              error={!!validationMessage(error, 'description')}
              margin="dense"
              id="description"
              label="Description"
              variant="outlined"
              value={description}
              fullWidth
              onChange={handleDescriptionChange}
              helperText={validationMessage(error, 'description')}
            />
            <Grid container columnSpacing={2} sx={{ marginTop: '10px' }}>
              <Grid item xs={4}>
                <UnitsSelect
                  id="selectMassUnits"
                  val={massUnits}
                  handler={setMassUnits}
                  items={getMassUnits()}
                  label="Mass units"
                />
              </Grid>
              <Grid item xs={4}>
                <UnitsSelect
                  id="selectDistanceUnits"
                  val={distanceUnits}
                  handler={setDistanceUnits}
                  items={getDistanceUnits()}
                  label="Distance units"
                />
              </Grid>
              <Grid item xs={4}>
                <UnitsSelect
                  id="selectSpeedUnits"
                  val={speedUnits}
                  handler={setSpeedUnits}
                  items={getSpeedUnits()}
                  label="Speed units"
                />
              </Grid>
            </Grid>
            <Typography variant="body2" mt={3}>
              To set the map&apos;s center when opening the project, drag the
              pin, or enter an address below and click &apos;ok&apos;.
            </Typography>
            <Geocoder setCenter={setCenter} />
          </Grid>
          <Grid item xs={6}>
            <div
              style={{
                height: '300px',
              }}
            >
              <ProjectDialogMap center={center} setCenter={setCenter} />
            </div>
          </Grid>
        </Grid>
        <Grid
          container
          rowSpacing={2}
          columnSpacing={2}
          direction="row"
          justifyContent="flex-end"
        >
          <Grid item>
            <Button onClick={close}>Cancel</Button>
          </Grid>
          <Grid item>
            <Button onClick={submit}>Ok</Button>
          </Grid>
        </Grid>
      </PaperStyled>
    </Modal>
  )
}
